/*!
  Pioneer WFRIA ProgressBar v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["ProgressBar"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("ProgressBar") < 0) {
    window.__PIONEER_GLOBAL__.components.push("ProgressBar");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["announcementDelay", "autoFocus", "clearAnnouncementDelay", "completionAnnouncement", "elementToFocusOnClose", "initialAnnouncement", "onComplete", "percentage", "aria-valuetext"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import LiveRegion from "../private/LiveRegion/LiveRegion";
import ProgressIndicator from "../private/ProgressIndicator/ProgressIndicator";
import { ARIA_VALUETEXT_ATTRIBUTE, PERCENTAGE_ANNOUNCEMENTS, PROGRESSBAR_ANNOUNCEMENT_DELAY, PROGRESSBAR_CLEAR_ANNOUNCEMENT_DELAY } from "./constants";
/**
 * Progress Bar is a stand-alone animated element that
 * communicates to the user that an action is happening.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

const ProgressBar = _ref => {
  let {
    announcementDelay,
    autoFocus,
    // eslint-disable-line jsx-a11y/no-autofocus
    clearAnnouncementDelay,
    completionAnnouncement,
    elementToFocusOnClose,
    // eslint-disable-line no-unused-vars
    initialAnnouncement,
    onComplete,
    percentage,
    "aria-valuetext": ariaValueText
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const mayAnnounce = useRef(autoFocus);
  const [remainingAnnouncements, setRemainingAnnouncements] = useState([...PERCENTAGE_ANNOUNCEMENTS]);
  const hasAnnouncement = percentage >= remainingAnnouncements[0];
  const shouldAnnounce = mayAnnounce.current && hasAnnouncement;
  useEffect(() => {
    while (percentage >= remainingAnnouncements[0]) {
      remainingAnnouncements.shift();
    }

    setRemainingAnnouncements(remainingAnnouncements);
  }, [percentage, remainingAnnouncements]);
  let onAnnouncementClear,
      assistiveContent = shouldAnnounce ? ariaValueText : "";
  /**
   * Forces announcements of "initialAnnouncement" and "completionAnnouncement" when passed.
   * "initialAnnouncement" will only be announced on initial mount of component.
   */

  if (initialAnnouncement && remainingAnnouncements[0] === 0) {
    assistiveContent = initialAnnouncement;
  } else if (completionAnnouncement) {
    assistiveContent = completionAnnouncement;
    onAnnouncementClear = onComplete;
  }

  const ariaProps = {
    [ARIA_VALUETEXT_ATTRIBUTE]: `${ariaValueText}% completed`
  };
  return _jsxs(React.Fragment, {
    children: [_jsx(ProgressIndicator, _objectSpread(_objectSpread({
      progress: percentage
    }, ariaProps), {}, {
      role: "progressbar",
      autoFocus: true // eslint-disable-line jsx-a11y/no-autofocus

    }, otherProps)), _jsx(LiveRegion, {
      politeness: "assertive",
      role: "status",
      onAnnouncementClear: onAnnouncementClear,
      clearAnnouncementDelay: clearAnnouncementDelay,
      announcementDelay: announcementDelay,
      children: assistiveContent
    })]
  });
};

ProgressBar.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The text that conveys the current progress information. */
  "aria-valuetext": PropTypes.string.isRequired,

  /**
   * The text to be announced once the ProgressBar mounts.
   */
  initialAnnouncement: PropTypes.string.isRequired,

  /**
   * Pass through for LiveRegion. The time after which the live region content should be announced.
   */
  announcementDelay: PropTypes.number,

  /**
   * Determines whether component will receive focus when mounted.
   * Also determines whether the Progress Bar is obtrusive or unobtrusive.
   * If it is true then Progress Bar will be obtrusive, otherwise unobtrusive.
   *
   * Obtrusive means that it announces the start of the task, completion of the
   * task, and in intervals on the page and will interrupt the user, with or without focus.
   *
   * Unobtrusive means that the Progress Bar announces the start of the task, completion of
   * the task, and announces the updates in intervals when it receives focused.
   */
  autoFocus: PropTypes.bool,

  /** The CSS class name of the root element. */
  className: PropTypes.string,

  /**
   * Pass through for LiveRegion. The time after which the live region content should be announced.
   */
  clearAnnouncementDelay: PropTypes.number,

  /**
   * The text to be announced once the request is completed.
   * This should only be passed when the ProgressBar progress has been completed. After this prop
   * is set then `onComplete` callback will fire.
   */
  completionAnnouncement: PropTypes.string,

  /**
   * This prop identifies the element that should receive
   * focus after ProgressBar is unmounted.
   * It expects a [ref](https://reactjs.org/docs/refs-and-the-dom.html).
   */
  elementToFocusOnClose: PropTypes.shape({
    current: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.instanceOf(React.Component)])
  }),

  /**
   * Callback function that is executed after the announcement of `completionAnnouncement` prop.
   * This can be used to hide or unmount the ProgressBar after it has completed all necessary announcements.
   */
  onComplete: PropTypes.func,

  /**
   * The percentage of completion. If it is passed then ProgressBar
   * would behave as determinate, otherwise it would be indeterminate.
   */
  percentage: PropTypes.number
} : {};
ProgressBar.defaultProps = {
  announcementDelay: PROGRESSBAR_ANNOUNCEMENT_DELAY,
  autoFocus: false,
  className: "",
  clearAnnouncementDelay: PROGRESSBAR_CLEAR_ANNOUNCEMENT_DELAY,
  completionAnnouncement: "",
  elementToFocusOnClose: null,
  onComplete: null,
  percentage: null
};
export default ProgressBar;